"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fullRequirementsItems = exports.basicRequirementsItems = exports.GROUP_LABELS = exports.DOCS_URL = void 0;
exports.DOCS_URL = 'https://docs.crazygames.com';
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_LOAD_TIME = 20;
exports.GROUP_LABELS = {
    account: {
        label: 'Adheres to Account integration requirements',
    },
    ads: { label: 'Adheres to Ads requirements' },
    multiplayer: {
        label: 'Adheres to multiplayer requirements',
        docsUrl: '/requirements/multiplayer/#requirements-for-online-with-friends',
    },
    iap: { label: 'Adheres to IAP requirements' },
};
exports.basicRequirementsItems = [
    {
        text: `Initial download size <${MAX_FILE_SIZE_MB}MB`,
        tooltip: `Based on files downloaded between the Play Now state and the first Gameplay Start event (SDK), else on total file size. Initial download size should be below ${MAX_FILE_SIZE_MB}MB. Initial download should be below 20MB fo the game to be eligible for mobile homepage`,
        type: 'automatic',
        key: 'initialDownloadSize',
        validationFunction: (data) => {
            if (data.initialLoadSizeBytes === null) {
                // if load size not detected, check total build size (if 0 then it's because it's an iframe)
                if (data.totalSizeBytes !== null && data.totalSizeBytes > 0) {
                    return data.totalSizeBytes < MAX_FILE_SIZE;
                }
                return null;
            }
            return data.initialLoadSizeBytes ? data.initialLoadSizeBytes < MAX_FILE_SIZE : false;
        },
    },
    {
        text: `Load time <${MAX_LOAD_TIME}s`,
        tooltip: `Initial load time below ${MAX_LOAD_TIME} seconds.`,
        type: 'automatic',
        key: 'initialLoadTime',
        validationFunction: (data) => {
            if (data.loadTimeSeconds !== null && data.loadTimeSeconds > 0) {
                return data.loadTimeSeconds < MAX_LOAD_TIME;
            }
            return null;
        },
    },
    {
        text: 'First gameplay start',
        tooltip: 'Gameplay start is mandatory and allows us to measure the initial download size',
        type: 'automatic',
        key: 'gameplayStart',
        isRequired: (isFullLaunch) => isFullLaunch,
        validationFunction: (data) => data.detectedSDKFunctionality.includes('gameplayStart'),
    },
    {
        text: 'First gameplay start implemented correctly',
        tooltip: 'When user first lands in a playable state',
        type: 'manual',
        key: 'firstGameplayStart',
        isRequired: (isFullLaunch) => isFullLaunch,
    },
    {
        text: 'Complies to Gameplay requirements',
        tooltip: 'Your game should look and work nice. Please check the docs link for more details.',
        type: 'manual',
        key: 'gameplayChecks',
        docsUrl: `${exports.DOCS_URL}/requirements/gameplay/#basic-gameplay-requirements`,
    },
    {
        text: 'Runs on all CrazyGames domains',
        tooltip: 'If your game is an iframe game, you may be using "Content-Security-Policy: frame-ancestors" to protect it from illegal embedding. Please check the docs link, and ensure that you whitelisted all the CrazyGames domains.',
        type: 'manual',
        key: 'runsOnCrazyGamesDomains',
        docsUrl: `${exports.DOCS_URL}/resources/html5-resources/#protecting-iframe-games`,
    },
    { text: 'Browser checks', tooltip: 'Game should work on Chrome and Edge', type: 'manual', key: 'chromeEdgeFriendly' },
    { text: 'Device checks:  Mobile', tooltip: 'Game plays smoothly on mobile devices', type: 'manual', key: 'mobileFriendly' },
    { text: 'No external ads', tooltip: 'Only ads served through the CrazyGames SDK are allowed', type: 'manual', key: 'noExternalAds' },
    {
        text: 'Does not offer external login options',
        tooltip: 'External login options (e.g. Facebook, Google, email) are not allowed.',
        type: 'manual',
        key: 'noExternalLogin',
        docsUrl: `${exports.DOCS_URL}/requirements/account-integration/`,
    },
    {
        text: 'InviteLink functionality used',
        type: 'automatic',
        key: 'inviteLinkFunctionalityUsed',
        group: 'multiplayer',
        validationFunction: (data) => {
            if (data.detectedSDKFunctionality.includes('inviteLink')) {
                return true;
            }
            return null;
        },
        docsUrl: `${exports.DOCS_URL}/sdk/game/#invite-link`,
    },
    {
        text: 'InviteButton functionality used',
        type: 'automatic',
        key: 'inviteButtonFunctionalityUsed',
        group: 'multiplayer',
        validationFunction: (data) => {
            if (data.detectedSDKFunctionality.includes('showInviteButton')) {
                return true;
            }
            return null;
        },
        docsUrl: `${exports.DOCS_URL}/sdk/game/#invite-button`,
    },
    {
        text: 'Game uses Invite Button correctly',
        type: 'manual',
        key: 'inviteButtonUsedCorrectly',
        group: 'multiplayer',
        docsUrl: `${exports.DOCS_URL}/sdk/game/#invite-button`,
    },
    {
        text: 'Game has chat functionality with a profanity filter and/or moderation',
        type: 'manual',
        key: 'hasChat',
        group: 'multiplayer',
    },
    {
        text: 'IsInstantMultiplayer supported',
        type: 'manual',
        key: 'isInstantMultiplayerSupported',
        group: 'multiplayer',
        docsUrl: `${exports.DOCS_URL}/sdk/game/#instant-multiplayer`,
        tooltip: 'You can click the test link to open the game with IsInstantMultiplayer=true.',
    },
];
exports.fullRequirementsItems = [
    {
        text: 'Implements gameplay start/stop events correctly (other than the initial gameplay start)',
        docsUrl: `${exports.DOCS_URL}/sdk/game/#gameplay-startstop`,
        type: 'manual',
        key: 'startsAndStopsGameplay',
    },
    {
        text: 'Lands user directly in gameplay',
        tooltip: 'Avoid showing any menus for the new users, and land them directly in gameplay.',
        type: 'manual',
        key: 'landsUserInGameplay',
    },
    {
        text: 'Game saves/loads progress correctly',
        tooltip: 'Progress save should work correctly. Consider using the data module from our SDK for progress save.',
        type: 'manual',
        key: 'savesAndLoadsProgress',
        isRequired: (isFullLaunch) => isFullLaunch,
    },
    { text: 'Device checks: Chromebook', tooltip: 'Game should work on Chromebooks with 4GB RAM', type: 'manual', key: 'chromebookFriendly' },
    {
        text: 'Adheres to advertisement requirements',
        type: 'manual',
        key: 'adheresToAdsRequirements',
        group: 'ads',
        docsUrl: `${exports.DOCS_URL}/requirements/ads/`,
    },
    {
        text: 'Midgame ad detected',
        type: 'automatic',
        key: 'midgameAd',
        validationFunction: (data) => {
            if (data.detectedSDKFunctionality.includes('requestMidgameAd')) {
                return true;
            }
            return null;
        },
        group: 'ads',
    },
    {
        text: 'Rewarded ad detected',
        type: 'automatic',
        key: 'rewardedAd',
        validationFunction: (data) => {
            if (data.detectedSDKFunctionality.includes('requestRewardedAd')) {
                return true;
            }
            return null;
        },
        group: 'ads',
    },
    {
        text: 'Banner detected',
        type: 'automatic',
        key: 'banner',
        validationFunction: (data) => {
            if (data.detectedSDKFunctionality.includes('requestBanner') || data.detectedSDKFunctionality.includes('requestResponsiveBanner')) {
                return true;
            }
            return null;
        },
        group: 'ads',
    },
    {
        text: 'Ads are placed according to requirements',
        type: 'manual',
        key: 'adsPlacedCorrectly',
        group: 'ads',
        docsUrl: `${exports.DOCS_URL}/requirements/ads/`,
    },
    {
        text: 'AdBlock supported',
        tooltip: 'Game runs even when an Adblock is enabled',
        type: 'manual',
        key: 'adBlockSupported',
        group: 'ads',
        isRequired: (isFullLaunch) => isFullLaunch,
    },
    {
        text: 'Progress is linked to CrazyGames Account',
        type: 'manual',
        key: 'progressLinkedToCzyAccount',
        group: 'account',
        docsUrl: `${exports.DOCS_URL}/requirements/account-integration/`,
    },
    {
        text: 'Uses CrazyGames username & avatar',
        type: 'manual',
        key: 'useCzyUsernameAndAvatar',
        group: 'account',
        docsUrl: `${exports.DOCS_URL}/requirements/account-integration/`,
    },
    {
        text: 'Automatic login for CrazyGames users (when using the getUserToken method)',
        type: 'manual',
        key: 'automaticLogicCzyUsers',
        group: 'account',
        docsUrl: `${exports.DOCS_URL}/requirements/account-integration/`,
    },
    {
        text: 'Requested Xsolla user token',
        type: 'automatic',
        key: 'getXsollaUserToken',
        group: 'iap',
        validationFunction: (data) => {
            if (data.detectedSDKFunctionality.includes('getXsollaUserToken')) {
                return true;
            }
            return null;
        },
        docsUrl: `${exports.DOCS_URL}/sdk/in-game-purchases/#get-xsolla-token`,
    },
    { text: 'IAP works correctly', type: 'manual', key: 'IAPWorksCorrectly', group: 'iap' },
];
